<script setup lang="ts">
import StarRating from './star-rating.vue';
import { computed } from 'vue';
import { Plural } from '@/shared/helpers';
import { useProductPage } from '~/features/product-page/business/use-product-page';
import { MMText, useMedia } from '@mm-frontend/mm-web-ui-kit';
import { useOtherReviewPopup } from '../../features';

interface IProductHeader {
  title: string;
  rating: number;
  reviews: number;
  ordersCount: number;
  reviewsWithPhotos: number;
}

const { state, setCurrentTabSlug } = useProductPage();
const { isMobileOrTablet } = useMedia();

const productHeader = computed<IProductHeader>(() => {
  return {
    title: state.product?.title || '',
    rating: state.product?.rating || 0,
    reviews: state.product?.feedbackQuantity || 0,
    ordersCount: state.product?.ordersAmount || 0,
    reviewsWithPhotos: state.reviewsPhotoAmount || 0,
  };
});

defineProps<{
  mobile?: boolean;
}>();

function scrollToReviews() {
  const FLOATING_CAP_HEIGHT = 108;

  if (isMobileOrTablet.value) {
    const element = document.getElementById('mobile-feedback');

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
      });
    }
    return;
  }

  const tabs = document.getElementById('description-tabs-wrapper');

  if (tabs) {
    const scrollTop = tabs.getBoundingClientRect().top + window.scrollY - FLOATING_CAP_HEIGHT;
    window.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }

  // Set active tab to reviews
  setCurrentTabSlug('Отзывы');
}

const { showOtherReviewPopup } = useOtherReviewPopup();
function actionClick() {
  if (state.product?.externalRating) {
    showOtherReviewPopup({
      rating: productHeader.value.rating,
      reviews: productHeader.value.reviews,
    });

    return;
  }

  scrollToReviews();
}

const ordersLabel = computed(() => {
  return 'Более ' + Plural.with(productHeader.value.ordersCount, 'заказа|заказов|заказов');
});
</script>

<template>
  <header v-if="state.product" class="product-header">
    <h1 class="title" data-test-id="text__product-name">
      {{ productHeader.title }}
    </h1>
    <div v-if="mobile" class="stats-mobile">
      <div @click="actionClick" class="stats-mobile-item">
        <div class="header">
          <MMText font="Body M/Semibold">{{ productHeader.rating.toFixed(1) }}</MMText>
          <star-rating :rating="productHeader.rating" />
        </div>
        <MMText font="Body M/Regular" tag="span" color="tertiary" class="subheader">
          {{
            state.product?.externalRating
              ? `${Plural.with(productHeader.reviews, 'оценка|оценки|оценок')} с других площадок`
              : Plural.with(productHeader.reviews, 'отзыв|отзыва|отзывов')
          }}
        </MMText>
      </div>
      <div class="stats-mobile-item">
        <div>
          <MMText font="Body M/Semibold" class="header">
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.9247 3.66699C14.7225 3.66699 15.4607 4.08947 15.8647 4.77736L16.6793 6.16394H18.75C20.5449 6.16394 22 7.61902 22 9.41394V18.417C22 20.2119 20.5449 21.667 18.75 21.667H5.25C3.45507 21.667 2 20.2119 2 18.417V9.41394C2 7.61902 3.45507 6.16394 5.25 6.16394H7.33042L8.205 4.73965C8.61425 4.07315 9.34026 3.66699 10.1224 3.66699H13.9247ZM13.9247 5.16699H10.1224C9.89892 5.16699 9.6892 5.26646 9.54793 5.43478L9.48325 5.52454L8.38913 7.30639C8.25271 7.52856 8.01071 7.66394 7.75 7.66394H5.25C4.2835 7.66394 3.5 8.44745 3.5 9.41394V18.417C3.5 19.3835 4.2835 20.167 5.25 20.167H18.75C19.7165 20.167 20.5 19.3835 20.5 18.417V9.41394C20.5 8.44745 19.7165 7.66394 18.75 7.66394H16.25C15.9841 7.66394 15.738 7.52312 15.6033 7.29382L14.5714 5.53712C14.4367 5.30782 14.1906 5.16699 13.9247 5.16699ZM12 9.16394C14.4853 9.16394 16.5 11.1787 16.5 13.6639C16.5 16.1492 14.4853 17.9139 12 17.9139C9.51472 17.9139 7.5 16.1492 7.5 13.6639C7.5 11.1787 9.51472 9.16394 12 9.16394ZM12 10.6639C10.3431 10.6639 9 12.0071 9 13.6639C9 15.3208 10.3431 16.6639 12 16.6639C13.6569 16.6639 15 15.3208 15 13.6639C15 12.0071 13.6569 10.6639 12 10.6639Z"
                fill="#1F2026"
              />
            </svg>
            {{ productHeader.reviewsWithPhotos }}
          </MMText>
        </div>
        <MMText font="Body M/Regular" tag="span" color="tertiary" class="subheader"
          >фото покупателей</MMText
        >
      </div>
      <div class="stats-mobile-item">
        <div>
          <MMText font="Body M/Semibold" class="header"> {{ productHeader.ordersCount }}+ </MMText>
        </div>
        <MMText font="Body M/Regular" tag="span" color="tertiary" class="subheader">заказов</MMText>
      </div>
    </div>
    <div v-else class="stats">
      <div @click="actionClick" class="rating">
        <star-rating :rating="productHeader.rating" />
        <span
          v-if="productHeader.rating"
          class="rating-value"
          data-test-id="text__product-rating"
          >{{ productHeader.rating.toFixed(1) }}</span
        >
      </div>
      <div class="stats-container">
        <div class="reviews">
          <span v-if="state.product?.externalRating" @click="actionClick" class="reviews_pointer">
            {{ Plural.with(productHeader.reviews, 'оценка|оценки|оценок') }} с других площадок
          </span>
          <template v-else>
            <span
              @click="actionClick"
              class="reviews_pointer"
              v-if="productHeader.reviews"
              data-test-id="text__quantity-of-reviews"
            >
              {{ Plural.with(productHeader.reviews, 'отзыв|отзыва|отзывов') }}
            </span>
            <span v-else>Нет отзывов</span>
          </template>
        </div>
        <div v-if="productHeader.ordersCount" class="orders">
          <div class="dot">.</div>
          <span data-test-id="text__product-orders">
            {{ ordersLabel }}
          </span>
        </div>
      </div>
    </div>
  </header>
</template>

<style lang="stylus" scoped>
.product-header
  font-family 'Inter'
  display flex
  flex-direction column

  &.floating
    .title
      font-size 16px
      line-height 16px

    .stats
      margin-top 0

  .title
    margin 0
    font-size 28px
    font-weight 600
    line-height 36px
    letter-spacing -0.011em
    word-break break-word

    +Media(Mobile, Tablet)
      font-size 20px
      line-height 28px

  .stats
    display flex
    flex-wrap wrap
    align-items center
    margin-top 12px

    .rating-value
      margin-left 12px

    .reviews, .orders, .rating-value
      font-weight 400
      font-size 14px
      line-height 20px
      color #8B8E99
      letter-spacing -0.005em

      &_pointer
        cursor pointer

    .rating
      display flex
      margin 6px 12px 0 0
      cursor pointer


    .stats-container
      display flex
      margin-top 6px

      .orders
        display flex

        .dot
          margin 0 6px
          line-height 13px

    .badge
      margin 6px 0 0 12px
      padding 4px 8px
      border-radius 12px
      font-weight 500
      font-size 13px
      line-height 15px
      color White()
      background-color hsla(128, 56%, 48%, 1)

.stats-mobile
  display: flex;
  align-items: flex-start;
  gap: 12px;
  overflow-x scroll
  margin: 12px -16px 0px -16px;
  padding 0 16px
  scroll-snap-type: x mandatory;
  scroll-padding-left: 16px

  &::-webkit-scrollbar {
    display: none;
  }

.stats-mobile-item
  font-family: Inter;
  display: flex;
  flex 0 1 145px
  flex-direction column
  justify-content center
  align-items flex-start
  height: 64px;
  padding: 4px 12px;
  gap: 4px;
  border-radius: 12px;
  border: 1px solid var(--light-border-subtle, rgba(54, 54, 64, 0.08));
  scroll-snap-align: start;
  box-sizing border-box

  .header
    display flex
    gap 6px
    align-items: center;

  .subheader
    white-space nowrap
</style>
